import { CSSProperties } from 'react';

import styles from './Gallery.module.css';
import { StoryDisplayOptions, Story } from './Story';
import { StoryAnimation } from '../enums';
import { useGetStoryModule } from '../hooks/module/useGetStoryModule';
import { RenameProperty } from '../types';
import { galleryJustifyContentMap } from '../widgets/gallery/constants';
import { GalleryJustify } from '../widgets/gallery/enums';
import { useGalleryAnimation } from '../widgets/gallery/hooks/useGalleryAnimation';

interface GallerySpecificDisplayOptions {
  gap: NonNullable<CSSProperties['gap']>;
  justify: GalleryJustify;
}

export interface GalleryDisplayOptions
  extends GallerySpecificDisplayOptions,
    RenameProperty<StoryDisplayOptions, 'withTitle', 'withStoryTitle'> {}

interface Props {
  id: string;
  company: string;
  displayOptions: GalleryDisplayOptions;
}

export const Gallery = ({ company, id, displayOptions }: Props) => {
  const { storyModule } = useGetStoryModule({
    id,
    company,
  });

  const {
    animatedStoryIndex,
    grabAnimation,
    restoreAnimation,
    registerStoryRef,
  } = useGalleryAnimation({
    id,
    galleryAnimations: displayOptions.animations,
    numberOfStories: storyModule?.Content.length || 0,
  });

  if (!storyModule) {
    return null;
  }

  return (
    <ul
      className={styles.gallery}
      style={{
        gap: displayOptions.gap,
        justifyContent: galleryJustifyContentMap[displayOptions.justify],
      }}
    >
      {storyModule.Content.map((storyComponent, i) => (
        <li
          key={storyComponent.Story?.id}
          className={styles.story}
          ref={(el) => registerStoryRef(i, el)}
        >
          <Story
            id={storyComponent.Story.id}
            company={company}
            product={storyModule.Product.Slug}
            extraDialogPayload={{
              company,
              product: storyModule.Product.Slug,
              moduleId: storyModule.id,
            }}
            isActive={animatedStoryIndex === i}
            displayOptions={{
              animations: displayOptions.animations,
              border: displayOptions.border,
              borderColor: displayOptions.borderColor,
              display: displayOptions.display,
              font: displayOptions.font,
              withPlayIcon: displayOptions.withPlayIcon,
              withRadius: displayOptions.withRadius,
              withShoppables: displayOptions.withShoppables,
              withTitle: displayOptions.withStoryTitle,
            }}
            onMouseEnter={
              displayOptions.animations.includes(StoryAnimation.OnHover)
                ? () => grabAnimation(i)
                : undefined
            }
            onMouseLeave={
              displayOptions.animations.includes(StoryAnimation.OnHover)
                ? restoreAnimation
                : undefined
            }
          />
        </li>
      ))}
    </ul>
  );
};
